import Section from 'components/shared/Section'
import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import Icon from 'components/shared/Icon'
import PersonTypePicker from './PersonTypePicker'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { removeCard, setPersonType } from 'store/unlimited-slice'
import CardOwnerData from './CardOwnerData'
import OwnerPhoto from './OwnerPhoto'
import UnlimitedCardTypePicker from './UnlimitedCardTypePicker'
import { useEffect } from 'react'
import { useAutoScroll } from 'utils/hooks'
import { DefaultTheme } from 'styled-components'
import { CardPersonDto } from 'store/api-slice/hooks'
import { useResolutions } from 'store/resolutions'

interface UnlimitedCardConfiguratorProps {
    index: number
    theme?: DefaultTheme
}

const UnlimitedCardConfigurator = ({
    index,
    theme,
}: UnlimitedCardConfiguratorProps) => {
    const { isMobile } = useResolutions()
    const { formatMessage } = useIntl()
    const { animatePendingScroll, scrollIndex, calculateScroll } =
        useAutoScroll()
    const dispatch = useAppDispatch()

    const { cardBoxes } = useAppSelector((state) => state.unlimited)

    const chosenPersonType = cardBoxes[index].personDetail?.key
    const chosenCardType = cardBoxes[index].cardDetail?.cardType
    const hasDarkMode = theme?.mode === 'dark'

    const handleRemoveCard = () => {
        dispatch(removeCard(index))
    }

    const handleSetPersonType = (personDetail: CardPersonDto) => {
        dispatch(setPersonType({ index, personDetail }))
        calculateScroll({
            trigerringSection: 'card_configuration',
            trigerringAction: 'show',
            targetId: `card_type-${index}`,
        })
    }

    useEffect(() => {
        animatePendingScroll(`cardIndexNumber${index}`)
    }, [scrollIndex])

    return (
        <Section id={`cardIndexNumber${index}`} hasNoSpacings>
            <Box
                direction="column"
                isFullWidth
                justify="space-between"
                align="flex-start"
                pt={[24, 56]}
                pr={[16, 80]}
                pl={[16, 80]}
                bgColor={hasDarkMode ? 'grayscale_0' : undefined}
                theme={theme}
                style={{
                    borderRadius: (() => {
                        if (hasDarkMode && chosenCardType) {
                            if (isMobile) {
                                return '6px 6px 16px 16px'
                            }
                            return '10px 10px 24px 24px'
                        }
                        return '10px'
                    })(),
                }}
            >
                <Box
                    direction="row"
                    isFullWidth
                    justify="space-between"
                    align="center"
                    mb={[32, 64]}
                >
                    <Text variant="heading2" theme={theme}>
                        {formatMessage({
                            id: 'annual.config.person_type_caption',
                            defaultMessage: 'Karta',
                        })}{' '}
                        {index + 1}
                    </Text>
                    {cardBoxes.length !== 1 && (
                        <Box
                            direction="row"
                            as="button"
                            onClick={handleRemoveCard}
                        >
                            <Text
                                variant="bodyStrong"
                                color="secondary_500"
                                mr={[10, 16]}
                                theme={theme}
                            >
                                {formatMessage({
                                    id: 'global.btn.remove',
                                    defaultMessage: 'Usuń',
                                })}
                            </Text>
                            <Icon
                                variant="bin"
                                size={[20, 24]}
                                color={
                                    hasDarkMode
                                        ? 'primary_500'
                                        : 'secondary_500'
                                }
                            />
                        </Box>
                    )}
                </Box>
                <Box mb={[24, 56]} isFullWidth>
                    <PersonTypePicker
                        index={index}
                        title={formatMessage({
                            id: 'tickets.person_section_caption',
                            defaultMessage: 'Wybierz typ gościa',
                        })}
                        selectedPerson={chosenPersonType}
                        setPerson={handleSetPersonType}
                        theme={theme}
                        testIdPrefix="unlimited"
                    />
                </Box>
                {!!chosenPersonType && (
                    <Box
                        mb={!chosenCardType ? [24, 56] : 0}
                        pb={chosenCardType && hasDarkMode ? [32, 50] : 0}
                        isFullWidth
                    >
                        <UnlimitedCardTypePicker
                            index={index}
                            personType={chosenPersonType}
                            theme={theme}
                        />
                    </Box>
                )}
            </Box>
            {chosenCardType && (
                <Box
                    direction="column"
                    isFullWidth
                    justify="space-between"
                    align="center"
                    pr={[16, 80]}
                    pb={[24, 56]}
                    pl={[16, 80]}
                >
                    <CardOwnerData index={index} />
                    <OwnerPhoto index={index} />
                </Box>
            )}
        </Section>
    )
}

export default UnlimitedCardConfigurator
